import logo from '../assets/logo_ekynair.png';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../constants/theme';
import { mainLine, tooltipTitle } from '../shared/buildInfo';

const Login = ({ onLoginSuccess, onSwitchToSignup, onForgotPassword, darkMode, verificationMessage, on2FA }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [message, setMessage] = useState(verificationMessage || null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [resending, setResending] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      await response.json();
      setMessage({ type: 'success', text: t('auth.login.msg.verificationResent') });
    } catch (error) {
      setMessage({ type: 'error', text: t('auth.login.msg.resendError') });
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...formData, rememberMe })
      });

      const data = await response.json();

      if (data.success) {
        // 2FA en attente — rediriger vers l'écran de saisie du code
        if (data.pending2FA) {
          if (on2FA) on2FA({ userId: data.userId });
          return;
        }
        // Stocker le token et les infos utilisateur
        // SEC-4 : le token JWT n'est plus stocké en localStorage pour le web
        // Les cookies httpOnly (access_token + refresh_token) gèrent la session
        // Le localStorage ne conserve que les données utilisateur non sensibles
        localStorage.setItem('ekynair_user', JSON.stringify(data.user));
        
        setMessage({ type: 'success', text: t('auth.login.msg.success') });
        
        setTimeout(() => {
          if (onLoginSuccess) onLoginSuccess(data.user);
        }, 500);
      } else {
        if (data.needsVerification) {
          setMessage({ type: 'error', text: t('auth.login.msg.needsVerification'), needsVerification: true });
        } else {
          setMessage({ type: 'error', text: `${data.message}` });
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('auth.login.msg.error') });
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    background: darkMode ? '#1E1E32' : '#fff',
    padding: '40px',
    borderRadius: '12px',
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e0e0e0',
    boxShadow: darkMode ? 'none' : '0 4px 12px rgba(0,0,0,0.1)',
    maxWidth: '450px',
    margin: '0 auto',
    width: '100%'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: '6px',
    border: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid #ddd',
    background: darkMode ? '#282845' : '#fff',
    color: darkMode ? '#E1E1E1' : '#2C3E50',
    fontSize: '14px',
    marginBottom: '16px'
  };

  const buttonStyle = {
    width: '100%',
    padding: '14px',
    background: loading ? '#999' : BRAND.gradient,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: loading ? 'not-allowed' : 'pointer',
    marginBottom: '16px'
  };

  const linkStyle = {
    color: darkMode ? '#90CAF9' : BRAND.blueText,
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: '14px'
  };

  return (
    <div style={{ padding: '40px 20px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img src={logo} alt="EkynAir" style={{ height: '80px', marginBottom: '12px' }} />
          <p style={{ color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px' }}>
            {t('auth.login.subtitle')}
          </p>
        </div>

        {message && (
          <div style={{
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            background: message.type === 'success' 
              ? (darkMode ? 'rgba(76, 175, 80, 0.2)' : '#e8f5e9')
              : (darkMode ? 'rgba(244, 67, 54, 0.2)' : '#ffebee'),
            color: message.type === 'success'
              ? (darkMode ? '#81c784' : '#2e7d32')
              : (darkMode ? '#ef5350' : '#c62828'),
            border: `1px solid ${message.type === 'success' ? '#4caf50' : '#f44336'}`
          }}>
            {message.text}
            {message.needsVerification && formData.email && (
              <div style={{ marginTop: '8px' }}>
                <span
                  onClick={handleResendVerification}
                  style={{ color: BRAND.blueText, cursor: resending ? 'not-allowed' : 'pointer', textDecoration: 'underline', fontSize: '13px' }}
                >
                  {resending ? t('auth.login.resending') : t('auth.login.resend')}
                </span>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px' }}>
              {t('signup.fields.email.label')}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('signup.fields.email.placeholder')}
              style={inputStyle}
              required
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px' }}>
              {t('signup.fields.password.label')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                style={{ ...inputStyle, paddingRight: '44px' }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? t('auth.login.togglePasswordAria.hide') : t('auth.login.togglePasswordAria.show')}
                style={{
                  position: 'absolute', right: '12px', top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: darkMode ? '#B8BFC7' : '#6B7280', padding: '4px',
                  display: 'flex', alignItems: 'center',
                }}
              >
                {showPassword ? (
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px', color: darkMode ? '#E8EDF2' : '#4A5568' }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ width: '14px', height: '14px', accentColor: '#1B77F1', cursor: 'pointer', flexShrink: 0 }}
              />
              {t('auth.login.rememberMe')}
            </label>
            <button type="button" onClick={onForgotPassword} style={{
              ...linkStyle, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit'
            }}>
              {t('auth.login.forgot')}
            </button>
          </div>

          <button type="submit" style={buttonStyle} disabled={loading}>
            {loading ? t('auth.login.submit.loading') : t('signup.login.cta')}
          </button>
        </form>

        <div style={{ textAlign: 'center', paddingTop: '20px', borderTop: darkMode ? '1px solid rgba(77, 198, 255, 0.2)' : '1px solid #e0e0e0' }}>
          <p style={{ color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '14px' }}>
            {t('auth.login.signupPrompt')}{' '}
            <button
              type="button"
              onClick={onSwitchToSignup}
              style={{
                background: 'none',
                border: 'none',
                color: darkMode ? '#90CAF9' : BRAND.blueText,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                padding: '4px 8px',
                borderRadius: '4px',
                fontFamily: 'inherit'
              }}
            >
              {t('auth.login.signupCta')}
            </button>
          </p>
        </div>

        <div title={tooltipTitle} style={{ textAlign: 'center', paddingTop: '20px', color: darkMode ? '#E8EDF2' : '#4A5568', fontSize: '12px' }}>
          {mainLine}
        </div>
      </div>
    </div>
  );
};

export default Login;
